import { CreateHomeSecurityPayload } from '@services/homeSecurity/homeSecurity.types';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const HomeSecurityContext = createContext<HomeSecurityContextType | undefined>(undefined);

export type HomeSecurityContextType = {
    signature: string | null;
    setSignature: (signature: string) => void;
    workStep: Array<string>;
    setWorkStep: (workStep: Array<string>) => void;
    passedModules: Array<HomeSecurityModule>;
    handleAddPassedModule: (module: HomeSecurityModule) => void;
    handleRemovePassedModule: (module: HomeSecurityModule) => void;
    selectedUser: string | null;
    setSelectedUser: (selectedUser: string) => void;
    isMinor: boolean;
    setIsMinor: (isMinor: boolean) => void;
    contractType: CreateHomeSecurityPayload['contract_type'] | null;
    setContractType: (contractType: CreateHomeSecurityPayload['contract_type']) => void;
    worksiteId: number | null;
    setWorksiteId: (worksiteId: number) => void;
    selectedEPIs: Array<string>;
    setSelectedEPIs: (selectedEPIs: Array<string>) => void;
    worksitePresentation: Array<string>;
    setWorksitePresentation: (worksitePresentation: Array<string>) => void;
    worksitePresentationComment: string;
    setWorksitePresentationComment: (worksitePresentationComment: string) => void;
    otherWorkStep: string;
    setOtherWorkStep: (otherWorkStep: string) => void;
};
type HomeSecurityProviderProps = {
    children: ReactNode;
};

export type HomeSecurityModule = 'tools' | 'worksite-presentation' | 'epis' | 'formation' | 'withdrawal';

export const HomeSecurityProvider: React.FC<HomeSecurityProviderProps> = ({ children }) => {
    const [signature, setSignature] = useState<string | null>(null);
    const [workStep, setWorkStep] = useState<Array<string>>([]);
    const [passedModules, setPassedModules] = useState<Array<HomeSecurityModule>>([]);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [isMinor, setIsMinor] = useState<boolean>(false);
    const [contractType, setContractType] = useState<CreateHomeSecurityPayload['contract_type'] | null>(null);
    const [worksiteId, setWorksiteId] = useState<number | null>(null);
    const [selectedEPIs, setSelectedEPIs] = useState<Array<string>>([]);
    const [worksitePresentation, setWorksitePresentation] = useState<Array<string>>([]);
    const [worksitePresentationComment, setWorksitePresentationComment] = useState<string>('');
    const [otherWorkStep, setOtherWorkStep] = useState<string>('');

    const location = useLocation();

    const handleAddPassedModule = (module: HomeSecurityModule) => {
        setPassedModules((prev) => [...prev, module]);
    };

    const handleRemovePassedModule = (module: HomeSecurityModule) => {
        setPassedModules((prev) => prev.filter((m) => m !== module));
    };

    const resetValues = () => {
        setSignature(null);
        setWorkStep([]);
        setPassedModules([]);
        setSelectedUser(null);
        setSelectedEPIs([]);
        setIsMinor(false);
        setContractType(null);
        setWorksiteId(null);
        setWorksitePresentation([]);
        setOtherWorkStep('');
    };

    useEffect(() => {
        if (!location.pathname.includes('/home-security')) {
            resetValues();
        }
    }, [location.pathname]);

    const value = {
        signature,
        setSignature,
        workStep,
        setWorkStep,
        passedModules,
        handleAddPassedModule,
        handleRemovePassedModule,
        selectedUser,
        setSelectedUser,
        isMinor,
        setIsMinor,
        contractType,
        setContractType,
        worksiteId,
        setWorksiteId,
        selectedEPIs,
        setSelectedEPIs,
        worksitePresentation,
        setWorksitePresentation,
        worksitePresentationComment,
        setWorksitePresentationComment,
        otherWorkStep,
        setOtherWorkStep,
    };
    return <HomeSecurityContext.Provider value={value}>{children}</HomeSecurityContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useHomeSecurity = () => {
    const context = useContext(HomeSecurityContext);
    if (context === undefined) {
        throw new Error('useHomeSecurity must be used within a HomeSecurityProvider');
    }

    return context;
};
